import { createElement, createContext, useContext, memo } from 'react';
import { fastCompare } from '@ux/perf';

var Compare;

(function (Compare) {
  Compare["FAST"] = "fast";
  Compare["SHALLOW"] = "shallow";
  Compare["NONE"] = "none";
})(Compare || (Compare = {}));

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var pipe = function pipe(fns) {
  return function (arg) {
    return fns.reduce(function (v, f) {
      return f(v);
    }, arg);
  };
};
var compose = function compose(fns) {
  return pipe(fns.slice().reverse());
};

// this is more of an escape hatch for 3rd parties using functions as classes
// if a component isn't functional (i.e. a memo component or a class)
// this will wrap it inside a functional component
// if we don't do this then we can't set the display name
// or pass it into additional hooks/hocs
// one caveat is if babel has already transpiled a class, we can't tell it's not just a function

var wrapClass = function wrapClass(C) {
  var Wrapper = function Wrapper(props) {
    return createElement(C, props);
  };

  Wrapper.displayName = C.displayName;
  return Wrapper;
};

var isFunction = function isFunction(fn) {
  return typeof fn === 'function';
};

var isClass = function isClass(fn) {
  return isFunction(fn) && Function.prototype.toString.call(fn).startsWith('class');
};

var isFunctional = function isFunctional(fn) {
  return isFunction(fn) && !isClass(fn);
}; // if a component isn't functional (i.e. a memo component or a class)
// this will wrap it inside a functional component
// if we don't do this then we can't set the display name
// or pass it into additional hooks/hocs
// one caveat is if babel has already transpiled a class, we can't tell it's not just a function


var ensureFunctional = function ensureFunctional(C) {
  if (isFunctional(C)) {
    return C;
  }

  return wrapClass(C);
};

var context = createContext({});
var Provider = context.Provider;
var Consumer = context.Consumer;
var getStubbedComponent = function getStubbedComponent(name, fallback) {
  var cache = useContext(context);
  return cache[name] || fallback;
};

var defaultStub = function defaultStub(props) {
  return createElement('div', props);
};

var stubComponent = function stubComponent(name) {
  var Stub = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : defaultStub;
  var cache = useContext(context);
  cache[name] = Stub;
};

var _compareMap;
var compareMap = (_compareMap = {}, _defineProperty(_compareMap, Compare.FAST, fastCompare), _defineProperty(_compareMap, Compare.SHALLOW, memo), _defineProperty(_compareMap, Compare.NONE, null), _compareMap);

var mush = function mush(name) {
  var compare = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Compare.FAST;
  return function () {
    var compareMethod = compareMap[compare];
    var enhancers = [];

    if (compareMethod) {
      enhancers.push(compareMethod);
    }

    for (var _len = arguments.length, hocs = new Array(_len), _key = 0; _key < _len; _key++) {
      hocs[_key] = arguments[_key];
    }

    if (hocs.length) {
      enhancers.push.apply(enhancers, hocs);
    }

    var enhancer = compose(enhancers);

    var enhance = function enhance(BaseComponent) {
      var Enhanced = ensureFunctional(enhancer(BaseComponent)); // we check at render time if the component has been stubbed
      // and if so, we instead render the stubbed component instead
      // in anything other than test mode this will be stripped out

      if (process.env.NODE_ENV === 'test') {
        var Original = Enhanced;

        Enhanced = function Enhanced(props) {
          var Stub = getStubbedComponent(name, Original);
          return createElement(Stub, props);
        };
      }

      Enhanced.displayName = name;
      return Enhanced;
    };

    if (process.env.NODE_ENV === 'test') {
      // eslint-disable-next-line no-underscore-dangle
      enhance.__hooks = hocs;
    }

    return enhance;
  };
};

var withHooks = function withHooks() {
  for (var _len = arguments.length, hooks = new Array(_len), _key = 0; _key < _len; _key++) {
    hooks[_key] = arguments[_key];
  }

  var hoc = function hoc(C) {
    // for backwards compatibility we want to fall back to the old array syntax
    if (Array.isArray(hooks[0])) {
      hooks = hooks[0]; // eslint-disable-line no-param-reassign
    }

    var Component = ensureFunctional(C);
    return pipe(hooks.concat(Component));
  };

  if (process.env.NODE_ENV === 'test') {
    // eslint-disable-next-line no-underscore-dangle
    hoc.__hooks = hooks;
  }

  return hoc;
};

/* global process */

var puree = function puree(name) {
  var compare = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : Compare.FAST;
  var mush$1 = mush(name, compare);
  return function () {
    for (var _len = arguments.length, hooks = new Array(_len), _key = 0; _key < _len; _key++) {
      hooks[_key] = arguments[_key];
    }

    // @ts-ignore - Expected at least 0 arguments but got 0 or more - what?
    var enhance = mush$1(withHooks.apply(void 0, hooks));

    if (process.env.NODE_ENV === 'test') {
      // @ts-ignore
      // eslint-disable-next-line no-underscore-dangle
      enhance.__hooks = hooks;
    }

    return enhance;
  };
};

var _compareMap$1;
/* eslint-enable max-len */

var compareMap$1 = (_compareMap$1 = {}, _defineProperty(_compareMap$1, Compare.FAST, fastCompare), _defineProperty(_compareMap$1, Compare.SHALLOW, memo), _defineProperty(_compareMap$1, Compare.NONE, function (Component) {
  return Component;
}), _compareMap$1); // eslint-disable-next-line max-len

var enhance = function enhance(name) {
  var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
    // eslint-disable-next-line
    console.warn('DEPRECATED: pureEnhance has been deprecated. Please use "puree" or "mush". This function will be removed in future versions');
  }

  if (Array.isArray(opts)) {
    // eslint-disable-next-line no-param-reassign
    opts = {
      hooks: opts
    };
  }

  var _opts = opts,
      hocs = _opts.hocs,
      hooks = _opts.hooks,
      pipe = _opts.pipe,
      _opts$compare = _opts.compare,
      compare = _opts$compare === void 0 ? Compare.FAST : _opts$compare;
  var compareMethod = compareMap$1[compare];
  var enhancers = [compareMethod];

  if (pipe && pipe.length) {
    enhancers.push.apply(enhancers, pipe);
  }

  if (hocs && hocs.length) {
    enhancers.push.apply(enhancers, hocs);
  }

  if (hooks && hooks.length) {
    enhancers.push(withHooks(hooks));
  }

  var enhancer = compose(enhancers);
  return function (BaseComponent) {
    var Enhanced = enhancer(BaseComponent);

    var Wrapper = function Wrapper(props) {
      return createElement(Enhanced, props);
    };

    Wrapper.displayName = name;
    return Wrapper;
  };
};

var getHooks = function getHooks(e) {
  // eslint-disable-next-line no-underscore-dangle
  return e.__hooks;
};

var withProvider = function withProvider(Provider, staticProps) {
  return function (Component) {
    return function (props) {
      return createElement(Provider, staticProps, createElement(Component, props));
    };
  };
};

export default enhance;
export { Compare, Provider as StubProvider, compose, getHooks, mush, pipe, puree, stubComponent, withHooks, withProvider, wrapClass };
